// ProductLedger.js - Complete with Print Functionality and Size/Length Filter
import React, { useState, useRef, useEffect } from "react";
import api from "../Service/api";
import "./ProductLedger.css";

function ProductLedger() {
  const [productName, setProductName] = useState("");
  const [productInput, setProductInput] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isPrinting, setIsPrinting] = useState(false);
  
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedLength, setSelectedLength] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [showSizeDropdown, setShowSizeDropdown] = useState(false);
  const [sizeInput, setSizeInput] = useState("");
  
  // Generate numbers from 16 to 50
  const generateNumberRange = (start, end) => {
    const numbers = [];
    for (let i = start; i <= end; i++) {
      numbers.push(i.toString());
    }
    return numbers;
  };
  
  const sizeOptions = generateNumberRange(16, 50); // For roll
  const lengthOptions = generateNumberRange(16, 50); // For reel/tota
  
  const printRef = useRef();
  const dropdownRef = useRef();
  const sizeDropdownRef = useRef();

  useEffect(() => {
    fetchAllProducts();
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    setFromDate(firstDay.toISOString().split('T')[0]);
    setToDate(lastDay.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (sizeDropdownRef.current && !sizeDropdownRef.current.contains(event.target)) {
        setShowSizeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (productName) {
      const product = allProducts.find(p => p.productName === productName);
      if (product && product.stockByWeight) {
        const category = product.category?.toLowerCase();
        setSelectedCategory(category);
      }
    }
  }, [productName, allProducts]);

  const fetchAllProducts = async () => {
    try {
      const res = await api.get("/product/names-prices");
      if (res.data.code === 1) {
        const products = res.data.additionalDetail || [];
        const uniqueProducts = [...new Map(products.map(p => [p.productName, p])).values()];
        setAllProducts(uniqueProducts);
      }
    } catch (err) {
      console.error("Failed to fetch products", err);
    }
  };

  const fetchLedger = async () => {
    if (!productName) {
      alert("Please select a product");
      return;
    }
    if (!fromDate || !toDate) {
      alert("Please select date range");
      return;
    }

    setLoading(true);
    try {
      const params = { productName, fromDate, toDate };
      if (selectedSize) {
        params.size = selectedSize;
      }
      if (selectedLength) {
        params.length = selectedLength;
      }
      
      const res = await api.get("/api/product-ledger/get", { params });
      
      if (res.data.code === 1) {
        setLedgerData(res.data.additionalDetail);
      } else {
        alert(res.data.message);
        setLedgerData(null);
      }
    } catch (err) {
      console.error("Failed to fetch ledger", err);
      alert("Failed to fetch ledger data");
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelect = (product) => {
    setProductName(product.productName);
    setProductInput(product.productName);
    setShowDropdown(false);
    setSelectedSize("");
    setSelectedLength("");
    setSizeInput("");
  };

  const handleSizeInputChange = (e) => {
    const value = e.target.value;
    setSizeInput(value);
    setShowSizeDropdown(true);
    if (value === "") {
      clearSizeFilter();
    }
  };

  const handleSizeSelect = (item) => {
    if (isReelOrTota) {
      setSelectedLength(item);
      setSizeInput(item + 'm');
    } else {
      setSelectedSize(item);
      setSizeInput(item);
    }
    setShowSizeDropdown(false);
  };

  const clearSizeFilter = () => {
    setSelectedSize("");
    setSelectedLength("");
    setSizeInput("");
    setShowSizeDropdown(false);
  };

  const filteredProducts = allProducts.filter(p =>
    p.productName.toLowerCase().includes(productInput.toLowerCase())
  );

  const isReelOrTota = selectedCategory === 'reel' || selectedCategory === 'tota';
  const isRoll = selectedCategory === 'roll';
  const showSizeFilter = productName && (isReelOrTota || isRoll);
  const filterItems = isReelOrTota ? lengthOptions : sizeOptions;
  const sizeFilterLabel = isReelOrTota ? 'Length (m)' : 'Size';
  const hasFilterApplied = selectedSize || selectedLength;

  const getFilteredItems = () => {
    if (!sizeInput) return filterItems;
    const searchValue = isReelOrTota ? sizeInput.replace('m', '') : sizeInput;
    return filterItems.filter(item => 
      item.toLowerCase().includes(searchValue.toLowerCase())
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0.00';
    return new Intl.NumberFormat('en-PK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  // ----- Updated badge functions to include FACTORY_USE and FACTORY_USE_RETURN -----
  const getTransactionBadgeClass = (type) => {
    switch(type) {
      case 'PURCHASE': return 'badge-purchase';
      case 'SALE': return 'badge-sale';
      case 'PURCHASE_RETURN': return 'badge-purchase-return';
      case 'SALE_RETURN': return 'badge-sale-return';
      case 'FACTORY_USE': return 'badge-factory-use';
      case 'FACTORY_USE_RETURN': return 'badge-factory-return';
      default: return '';
    }
  };

  const getTransactionBadgeText = (type) => {
    switch(type) {
      case 'PURCHASE': return '📥 Purchase';
      case 'SALE': return '📤 Sale';
      case 'PURCHASE_RETURN': return '↩️ Purchase Return';
      case 'SALE_RETURN': return '↪️ Sale Return';
      case 'FACTORY_USE': return '🏭 Factory Use';
      case 'FACTORY_USE_RETURN': return '🔄 Factory Return';
      default: return type;
    }
  };

  const getPrintBadgeClass = (type) => {
    switch(type) {
      case 'PURCHASE': return 'badge-purchase-print';
      case 'SALE': return 'badge-sale-print';
      case 'PURCHASE_RETURN': return 'badge-purchase-return-print';
      case 'SALE_RETURN': return 'badge-sale-return-print';
      case 'FACTORY_USE': return 'badge-factory-use-print';
      case 'FACTORY_USE_RETURN': return 'badge-factory-return-print';
      default: return '';
    }
  };

  const getPrintBadgeText = (type) => {
    switch(type) {
      case 'PURCHASE': return '📥 Purchase';
      case 'SALE': return '📤 Sale';
      case 'PURCHASE_RETURN': return '↩️ Purchase Return';
      case 'SALE_RETURN': return '↪️ Sale Return';
      case 'FACTORY_USE': return '🏭 Factory Use';
      case 'FACTORY_USE_RETURN': return '🔄 Factory Return';
      default: return type;
    }
  };

  const handlePrint = () => {
    if (isPrinting || !ledgerData) return;
    setIsPrinting(true);
    
    const printWindow = window.open('', '_blank');
    const filterText = selectedSize ? `Size: ${selectedSize}` : (selectedLength ? `Length: ${selectedLength}` : '');
    
    // Compute running total for print
    let runningTotal = 0;
    const printRows = ledgerData.transactions && ledgerData.transactions.length > 0
      ? ledgerData.transactions.map(tx => {
          runningTotal += Number(tx.quantity || 0);
          return `
            <tr>
              <td>${formatDate(tx.date)}</td>
              <td><span class="badge-print ${getPrintBadgeClass(tx.transactionType)}">${getPrintBadgeText(tx.transactionType)}</span></td>
              <td>${tx.partyName || '-'}</td>
              <td>${tx.size ? tx.size : ''}${tx.size && tx.length ? ' | ' : ''}${tx.length ? tx.length : '-'}</td>
              <td>${tx.invoiceNumber || '-'}</td>
              <td class="text-right">${tx.quantity > 0 ? '+' : ''}${formatNumber(tx.quantity)}</td>
              <td class="text-right">${formatNumber(runningTotal)}</td>
              <td class="text-right">${formatNumber(tx.rate)}</td>
              <td class="text-right">${formatNumber(Math.abs(tx.totalAmount))}</td>
            </tr>
          `;
        }).join('')
      : '<tr><td colspan="9" style="text-align:center">No transactions found</td></tr>';
    
    // Build factory summary if available
    let factorySummary = '';
    if (ledgerData.totalFactoryUsed !== undefined || ledgerData.totalFactoryReturned !== undefined) {
      factorySummary = `
        <div><strong>Factory Used:</strong><br>${formatNumber(ledgerData.totalFactoryUsed || 0)}</div>
        <div><strong>Factory Returned:</strong><br>${formatNumber(ledgerData.totalFactoryReturned || 0)}</div>
      `;
    }
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Product Ledger - ${ledgerData.productName}</title>
          <meta charset="UTF-8">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; }
            .print-header { text-align: center; margin-bottom: 25px; padding-bottom: 15px; border-bottom: 2px solid #333; }
            .print-header h1 { font-size: 24px; margin-bottom: 8px; }
            .product-info { background: #f5f5f5; padding: 12px; margin-bottom: 20px; border-radius: 6px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 13px; }
            th, td { border: 1px solid #ddd; padding: 10px 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .text-right { text-align: right; }
            .summary-section { margin-top: 25px; padding: 15px; background: #f9f9f9; border: 1px solid #ddd; }
            .summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 15px; margin-top: 10px; }
            .footer { margin-top: 30px; text-align: center; font-size: 11px; color: #888; padding-top: 15px; border-top: 1px solid #ddd; }
            .badge-print { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; }
            .badge-purchase-print { background: #d4edda; color: #155724; }
            .badge-sale-print { background: #f8d7da; color: #721c24; }
            .badge-purchase-return-print { background: #fff3cd; color: #856404; }
            .badge-sale-return-print { background: #d1ecf1; color: #0c5460; }
            .badge-factory-use-print { background: #e2e3e5; color: #383d41; }
            .badge-factory-return-print { background: #d6d8db; color: #1b1e21; }
          </style>
        </head>
        <body>
          <div>
            <div class="print-header">
              <h1>Product Ledger Report</h1>
              <p>${ledgerData.productName}</p>
              <p>Period: ${formatDate(ledgerData.fromDate)} - ${formatDate(ledgerData.toDate)}</p>
              ${filterText ? `<p>Filter: ${filterText}</p>` : ''}
              <p>Generated on: ${new Date().toLocaleString()}</p>
            </div>
            <div class="product-info">
              <p><strong>Product:</strong> ${ledgerData.productName}</p>
              <p><strong>Report Period:</strong> ${formatDate(ledgerData.fromDate)} to ${formatDate(ledgerData.toDate)}</p>
              ${filterText ? `<p><strong>Filter:</strong> ${filterText}</p>` : ''}
            </div>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Party</th>
                  <th>Size</th>
                  <th>Invoice</th>
                  <th class="text-right">Qty</th>
                  <th class="text-right">Running Qty</th>
                  <th class="text-right">Rate</th>
                  <th class="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                ${printRows}
              </tbody>
            </table>
            <div class="summary-section">
              <h3>Summary</h3>
              <div class="summary-grid">
                <div><strong>Opening Stock:</strong><br>${formatNumber(ledgerData.openingStock)}</div>
                <div><strong>Total Purchased:</strong><br>${formatNumber(ledgerData.totalPurchased)}<br><small>Returned: ${formatNumber(ledgerData.totalPurchaseReturned || 0)}</small></div>
                <div><strong>Total Sold:</strong><br>${formatNumber(ledgerData.totalSold)}<br><small>Returned: ${formatNumber(ledgerData.totalSaleReturned || 0)}</small></div>
                ${factorySummary}
                <div><strong>Closing Stock:</strong><br>${formatNumber(ledgerData.closingStock)}</div>
              </div>
            </div>
            <div class="footer">
              <p>Computer generated report - No signature required</p>
            </div>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
      printWindow.onafterprint = () => {
        printWindow.close();
        setIsPrinting(false);
      };
    };
  };

  const filteredSizeItems = getFilteredItems();

  return (
    <div className="product-ledger-container">
      <h2 className="page-title">Product Ledger</h2>

      <div className="filter-card">
        <div className="filter-row">
          <div className="filter-group" ref={dropdownRef}>
            <label>Product Name *</label>
            <div className="searchable-dropdown">
              <input
                type="text"
                className="filter-input"
                value={productInput}
                onChange={(e) => {
                  setProductInput(e.target.value);
                  setShowDropdown(true);
                  if (e.target.value === "") setProductName("");
                }}
                onFocus={() => setShowDropdown(true)}
                placeholder="Search or select product..."
                autoComplete="off"
              />
              {showDropdown && filteredProducts.length > 0 && (
                <div className="dropdown-list">
                  {filteredProducts.map((p, idx) => (
                    <div
                      key={idx}
                      className="dropdown-item"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleProductSelect(p);
                      }}
                    >
                      <div className="product-name">{p.productName}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {showSizeFilter && (
            <div className="filter-group" ref={sizeDropdownRef}>
              <label>{sizeFilterLabel}</label>
              <div className="searchable-dropdown">
                <input
                  type="text"
                  className="filter-input"
                  value={sizeInput}
                  onChange={handleSizeInputChange}
                  onFocus={() => setShowSizeDropdown(true)}
                  placeholder={isReelOrTota ? "Filter by length..." : "Filter by size..."}
                  autoComplete="off"
                />
                {showSizeDropdown && filteredSizeItems.length > 0 && (
                  <div className="dropdown-list">
                    <div
                      className="dropdown-item"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        clearSizeFilter();
                      }}
                    >
                      <div className="product-name">All</div>
                    </div>
                    {filteredSizeItems.map((item, idx) => (
                      <div
                        key={idx}
                        className="dropdown-item"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSizeSelect(item);
                        }}
                      >
                        <div className="product-name">{item}{isReelOrTota ? ' m' : ''}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="filter-group">
            <label>From Date *</label>
            <input
              type="date"
              className="filter-input"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>To Date *</label>
            <input
              type="date"
              className="filter-input"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>

          <div className="filter-actions">
            <button className="btn-primary" onClick={fetchLedger} disabled={loading}>
              {loading ? 'Loading...' : 'Show Ledger'}
            </button>
            {ledgerData && (
              <button className="btn-print" onClick={handlePrint} disabled={isPrinting}>
                Print
              </button>
            )}
          </div>
        </div>
        
        {hasFilterApplied && (
          <div className="applied-filters">
            <span className="filter-tag">
              {isReelOrTota ? 'Length:' : 'Size:'} {selectedSize || selectedLength}
              <button className="filter-tag-remove" onClick={clearSizeFilter}>×</button>
            </span>
          </div>
        )}
      </div>

      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading ledger data...</p>
        </div>
      )}

      {!loading && ledgerData && (
        <>
          <div className="summary-cards">
            <div className="summary-card">
              <div className="summary-label">Opening Stock</div>
              <div className="summary-value">{formatNumber(ledgerData.openingStock)}</div>
            </div>
            <div className="summary-card purchase">
              <div className="summary-label">Total Purchased</div>
              <div className="summary-value">{formatNumber(ledgerData.totalPurchased)}</div>
              <div className="summary-sub">Returned: {formatNumber(ledgerData.totalPurchaseReturned || 0)}</div>
            </div>
            <div className="summary-card sale">
              <div className="summary-label">Total Sold</div>
              <div className="summary-value">{formatNumber(ledgerData.totalSold)}</div>
              <div className="summary-sub">Returned: {formatNumber(ledgerData.totalSaleReturned || 0)}</div>
            </div>
            {/* NEW: Factory summary cards, if data exists */}
            {(ledgerData.totalFactoryUsed !== undefined || ledgerData.totalFactoryReturned !== undefined) && (
              <>
                <div className="summary-card factory-use">
                  <div className="summary-label">Factory Used</div>
                  <div className="summary-value">{formatNumber(ledgerData.totalFactoryUsed || 0)}</div>
                </div>
                <div className="summary-card factory-return">
                  <div className="summary-label">Factory Returned</div>
                  <div className="summary-value">{formatNumber(ledgerData.totalFactoryReturned || 0)}</div>
                </div>
              </>
            )}
            <div className="summary-card closing">
              <div className="summary-label">Closing Stock</div>
              <div className="summary-value">{formatNumber(ledgerData.closingStock)}</div>
            </div>
          </div>

          <div className="table-container">
            <table className="ledger-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Party Name</th>
                  <th>Size</th>
                  <th>Invoice</th>
                  <th className="text-right">Quantity</th>
                  <th className="text-right">Running Qty</th>
                  <th className="text-right">Rate</th>
                  <th className="text-right">Total</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {ledgerData.transactions && ledgerData.transactions.length > 0 ? (
                  (() => {
                    let runningTotal = 0;
                    return ledgerData.transactions.map((tx, idx) => {
                      runningTotal += Number(tx.quantity || 0);
                      return (
                        <tr key={idx}>
                          <td>{formatDate(tx.date)}</td>
                          <td>
                            <span className={`badge ${getTransactionBadgeClass(tx.transactionType)}`}>
                              {getTransactionBadgeText(tx.transactionType)}
                            </span>
                          </td>
                          <td>{tx.partyName || '-'}</td>
                          <td>
                            {tx.size ? `${tx.size}` : ''}
                            {tx.size && tx.length ? ' | ' : ''}
                            {tx.length ? `${tx.length}` : '-'}
                            {!tx.size && !tx.length ? '-' : ''}
                          </td>
                          <td>{tx.invoiceNumber || '-'}</td>
                          <td className={`text-right ${tx.quantity < 0 ? 'text-danger' : 'text-success'}`}>
                            {tx.quantity > 0 ? `+${formatNumber(tx.quantity)}` : formatNumber(tx.quantity)}
                          </td>
                          <td className="text-right">{formatNumber(runningTotal)}</td>
                          <td className="text-right">{formatNumber(tx.rate)}</td>
                          <td className="text-right">{formatNumber(Math.abs(tx.totalAmount))}</td>
                          <td className="description-cell">{tx.description || '-'}</td>
                        </tr>
                      );
                    });
                  })()
                ) : (
                  <tr>
                    <td colSpan="10" className="empty-row">No transactions found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'none' }}>
            <div ref={printRef}></div>
          </div>
        </>
      )}

      {!loading && !ledgerData && productName && (
        <div className="empty-state">
          <p>No ledger data found. Please try different filters.</p>
        </div>
      )}
    </div>
  );
}

export default ProductLedger;